import dayjs from "dayjs";

import { swSettings } from "app/common/services/swSettings";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { IntentCell } from "app/components/React/Table/cells/IntentCell";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { i18nFilter } from "app/filters/i18nFilter";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import CoreTrendsBarCell from "pro-features/components/core cells/src/CoreTrendsBarCell/CoreTrendsBarCell";
import type { ITrendsBarValue } from "pro-features/components/TrendsBar/src/TrendsBar";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    DefaultCellRightAlign,
    IndexCell,
    LeadingSite,
    RelevancyCell,
    SearchKeywordCell,
    ProgressBarPercentCell,
} from "../../../../../../components/React/Table/cells";
import { RowSelectionConsumer } from "../../../../../../components/React/Table/cells/RowSelection";
import {
    DefaultCellHeader,
    DefaultCellHeaderRightAlign,
    HeaderCellBlank,
} from "../../../../../../components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "../../../../../../components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { minAbbrNumberFilter } from "../../../../../../filters/ngFilters";

const DEFAULT_SORT_FIELD = "score";
const DEFAULT_SORT_DIRECTION = "desc";
const dateFormat = (date) => dayjs(date).format("MMM, YYYY");

export const RelatedTableSettings = {
    defaultSortField: (duration) => DEFAULT_SORT_FIELD,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    getColumns: (sortedColumn: { field: string; sortDirection: string }, webSource: string, country, duration) => {
        const durationObject = DurationService.getDurationData(duration);
        const isWindowDuration = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
        const isWindow = durationObject.forAPI.isWindow;

        return [
            {
                fixed: true,
                cellComponent: RowSelectionConsumer,
                sortable: false,
                headerComponent: SelectAllRowsHeaderCellConsumer,
                isResizable: false,
                width: 50,
                disableHeaderCellHover: true,
            },
            {
                fixed: true,
                cellComponent: IndexCell,
                headerComponent: HeaderCellBlank,
                disableHeaderCellHover: true,
                sortable: false,
                isResizable: false,
                width: 65,
            },
            {
                fixed: true,
                field: "keyword",
                sortable: true,
                showTotalCount: true,
                cellComponent: (props) => (
                    <SearchKeywordCell
                        {...props}
                        destinationState={
                            swSettings.components.KeywordAnalysisV2Organic
                                ? "organicsearch_keywordAnalysis_2"
                                : undefined
                        }
                        destinationStateParams={{ selectedPageTab: EKeywordTabs[EKeywordTabs.Total] }}
                        adsUrl={proNavigator.href("paidsearch_keywordAnalysis_serp_ads", {
                            webSource,
                            country,
                            duration,
                            keyword: props.value,
                        })}
                    />
                ),
                displayName: i18nFilter()("keyword.generator.tool.table.column.keyword"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.keyword.tooltip"),
                width: 258,
                groupKey: "keywordGeneratorToolRelated",
            },
            {
                field: "score",
                sortable: true,
                cellComponent: RelevancyCell,
                displayName: i18nFilter()("keyword.generator.tool.table.column.score"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.keyword.score"),
                width: 140,
                groupKey: "keywordGeneratorToolRelated",
            },
            isWindowDuration
                ? {
                      field: "windowVolume",
                      sortable: true,
                      displayName: i18nFilter()("website.keywords.2.0.table.window.volume.column"),
                      tooltip: i18nFilter()("website.keywords.2.0.table.window.volume.column.tooltip"),
                      cellComponent: DefaultCellRightAlign,
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "minAbbrNumber:50",
                      minWidth: 130,
                      groupKey: "keywordGeneratorToolRelated",
                  }
                : {
                      field: "monthlyVolume",
                      sortable: true,
                      displayName: i18nFilter()("keyword.generator.tool.table.table.column.volume"),
                      tooltip: i18nFilter()("keyword.generator.tool.table.column.volume.tooltip"),
                      cellComponent: DefaultCellRightAlign,
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "minAbbrNumber:50",
                      minWidth: 140,
                      groupKey: "keywordGeneratorToolRelated",
                  },
            {
                field: "averageVolume",
                displayName: i18nFilter()("website.keywords.2.0.table.window.average.volume.column"),
                tooltip: i18nFilter()("website.keywords.2.0.table.window.average.volume.column.tooltip"),
                cellComponent: DefaultCellRightAlign,
                headerComponent: DefaultCellHeaderRightAlign,
                format: "minAbbrNumber:50",
                width: 100,
                groupKey: "keywordGeneratorToolRelated",
            },
            isWindow
                ? {
                      field: "VolumeChange",
                      displayName: i18nFilter()("website.keywords.2.0.table.window.volume.change.column"),
                      tooltip: i18nFilter()("website.keywords.2.0.table.volume.change.column.tooltip"),
                      cellComponent: (props) => {
                          const { change } = props.value;
                          const absChange = Math.abs(change);
                          const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;
                          return (
                              <ChangeStateCell
                                  {...props}
                                  undefinedSign={"-"}
                                  isPercentage={true}
                                  cellFilter={`simplePercentage:${precision}`}
                              />
                          );
                      },
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "percentagesign",
                      sortable: false,
                      width: 105,
                  }
                : null,
            {
                field: "volumeTrend",
                displayName: i18nFilter()("keyword.generator.tool.table.column.volumetrend"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.volumetrend.tooltip"),
                cellComponent: ({ value }) => {
                    if (value) {
                        const data = Object.keys(value)
                            .sort()
                            .map((item) => ({
                                value: value[item],
                                tooltip: (
                                    <span>
                                        <strong>
                                            {minAbbrNumberFilter()(value[item], THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE)}
                                        </strong>
                                        {` searches in ${dateFormat(item)}`}
                                    </span>
                                ),
                            })) as ITrendsBarValue[];
                        return <CoreTrendsBarCell value={data} />;
                    } else {
                        return "N/A";
                    }
                },
                format: "swPosition",
                width: 130,
                groupKey: "keywordGeneratorToolRelated",
            },
            {
                field: "latestZeroClicks",
                sortable: false,
                displayName: i18nFilter()("keyword.generator.tool.table.column.zero.click"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.zero.clicks.tooltip"),
                cellComponent: (props) => <ProgressBarPercentCell value={props.value} />,
                width: 170,
            },
            {
                field: "difficulty",
                automationKey: "Difficulty",
                trackingName: "Keyword Difficulty",
                displayName: i18nFilter()("analysis.source.search.all.table.columns.keyword.difficulty.title"),
                tooltip: i18nFilter()("analysis.source.search.all.table.columns.keyword.difficulty.tooltip", {
                    date: durationObject.raw.to.format("MMM YYYY"),
                }),
                width: 68,
                cellComponent: KeywordDifficultyCell,
                sortable: true,
                groupKey: "keywordGeneratorTool",
            },
            {
                field: "intents",
                trackingName: "Keyword Intent",
                displayName: i18nFilter()("keywords.table.header.intent.display.name"),
                tooltip: i18nFilter()("keywords.table.header.intent.tooltip"),
                width: 120,
                cellComponent: IntentCell,
            },
            {
                field: "cpc",
                sortable: true,
                displayName: i18nFilter()("keyword.generator.tool.table.column.cpc"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.cpc.tooltip"),
                cellComponent: DefaultCellRightAlign,
                headerComponent: DefaultCellHeaderRightAlign,
                format: "cpc",
                width: 86,
                groupKey: "keywordGeneratorToolRelated",
            },
            {
                width: 200,
                sortable: false,
                field: "leadingSite",
                displayName: i18nFilter()("keyword.generator.tool.table.column.website"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.website.tooltip"),
                cellComponent: LeadingSite,
                groupKey: "keywordGeneratorToolRelated",
            },
        ]
            .filter((column) => column)
            .map((col) => {
                const columnSort = sortedColumn || {
                    field: isWindowDuration ? "windowVolume" : "monthlyVolume",
                    sortDirection: DEFAULT_SORT_DIRECTION,
                };
                const isSorted = columnSort && col.field === columnSort.field;
                return {
                    visible: true,
                    headerComponent: DefaultCellHeader,
                    isSorted,
                    sortDirection: isSorted ? columnSort.sortDirection : DEFAULT_SORT_DIRECTION,
                    ...col,
                };
            });
    },
};
